import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import type { Invoice, TenantId } from '@etiquetachef/types';
import { cancelInvoiceCallable } from '@/lib/callables';
import { trackEvents } from '@/lib/events';
import { gatewayErrorMessage, gatewayOwnedInvoiceWarning } from './gatewayErrors';

type Props = {
  open: boolean;
  tenantId: TenantId;
  invoice: Invoice | null;
  onClose: () => void;
};

/**
 * Diálogo "Cancelar fatura" do admin.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200.
 *
 * Contrato: chama `cancelInvoice`. Fatura do gateway (`isGatewayOwnedInvoice`)
 * → aviso e confirmar desabilitado; o servidor recusa do mesmo jeito
 * (`failed-precondition`, `details.code: gateway_owned_invoice`), e essa recusa
 * sai traduzida (`gatewayErrorMessage`).
 *
 * O servidor exige `ADMIN_PERMISSIONS.cancelInvoice` (ROOT/FINANCE,
 * `apps/functions/src/auth/permissions.ts`) ou ADMIN do CRM com acesso ao tenant.
 *
 * Usado por: `InvoiceActions` (rota `/billing/invoices/:tenantId/:invoiceId`,
 * detalhe da fatura) e `routes/InvoicesPage.tsx` (rotas `/billing/invoices` e
 * `/billing/invoices/:tenantId`, menu Cobrança → Faturas) — admin web.
 * Spec: `docs/screens/billing-invoices.md`.
 *
 * Histórico:
 *  - 2026-10-03 18:22 +0200 — (#795, BIL-4) aviso + bloqueio para fatura do
 *    gateway; recusa do servidor traduzida pelo `details.code`.
 *  - 2026-10-10 14:40 +0200 — (#798) "Usado por" corrigido: `InvoiceActions`
 *    só vive no detalhe da fatura, não no do tenant; cabeçalho padrão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function CancelInvoiceDialog({
  open,
  tenantId,
  invoice,
  onClose,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  if (!invoice) return null;

  // #795 (BIL-4, 2026-10-03 18:22 +0200): cancelar só aqui uma fatura do
  // Stripe/Asaas não parava a cobrança. O servidor recusa; aqui o operador é
  // avisado antes de escrever o motivo.
  const gatewayWarning = gatewayOwnedInvoiceWarning(invoice, 'cancel', t);

  function close(): void {
    setError(null);
    onClose();
  }

  /**
   * Envia o cancelamento ao `cancelInvoice` e registra o evento de telemetria.
   *
   * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16).
   *
   * Contrato: no-op se a fatura é do gateway (o aviso já desabilitou o botão);
   * motivo vazio → erro local, sem rede. Sucesso → `trackEvents.invoiceCancelled`
   * e fecha. Erro → `gatewayErrorMessage` (recusa `gateway_owned_invoice`) ou
   * `err.message`, num `Alert` dentro do modal (#1021). O servidor é idempotente (fatura já CANCELLED não reescreve)
   * e recusa status ≠ PENDING e `amount_paid > 0` (M-26).
   *
   * Dados — input do callable: `{ "tenantId": "tenant-1", "invoiceId": "inv-1",
   * "reason": "..." }` (`CancelInvoiceInput`, `@etiquetachef/types`). Efeito no
   * servidor: `tenants/{tid}/invoices/{id}.status = CANCELLED` + audit
   * `INVOICE_CANCELLED`.
   *
   * Usado por: botão "Confirmar" deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function handleSubmit(): Promise<void> {
    if (!invoice || gatewayWarning !== null) return;
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await cancelInvoiceCallable({
        tenantId,
        invoiceId: invoice.id,
        reason: reason.trim(),
      });
      trackEvents.invoiceCancelled(tenantId, invoice.id, reason.trim());
      close();
    } catch (err) {
      setError(
        gatewayErrorMessage(err, t) ??
          (err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric')),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('billing.cancelInvoice.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
              disabled={gatewayWarning !== null}
            >
              {t('billing.cancelInvoice.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('billing.cancelInvoice.description', { invoice: invoice.invoice_number })}
        </Box>
        {gatewayWarning !== null && <Alert type="warning">{gatewayWarning}</Alert>}
        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
              setError(null);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
